
import React from "react";
import {
    BadgePercent,
    MapPin,
    ShieldCheck,
    Headphones,
    Building2,
    HeartHandshake,
    CheckCircle2,
    Sparkles,
} from "lucide-react";

const benefits = [
    {
        icon: BadgePercent,
        title: "Great Hotel Deals",
        description:
            "Explore affordable hotel options in Dubai and find a stay that suits your travel budget.",
    },
    {
        icon: MapPin,
        title: "Prime Dubai Locations",
        description:
            "Discover hotels near popular attractions, shopping destinations, beaches and business districts.",
    },
    {
        icon: ShieldCheck,
        title: "Confident Booking",
        description:
            "Review hotel details, compare options and choose the accommodation that's right for you.",
    },
    {
        icon: Headphones,
        title: "Helpful Travel Support",
        description:
            "Find the information you need to plan your Dubai hotel stay with greater ease.",
    },
    {
        icon: Building2,
        title: "Hotels for Every Traveler",
        description:
            "From budget-friendly city hotels to relaxing beachfront stays, explore a variety of options.",
    },
    {
        icon: HeartHandshake,
        title: "Guest-Focused Experience",
        description:
            "Enjoy a simple hotel search experience designed to make planning your trip easier.",
    },
];

function BenefitCard({ benefit, index }) {
    const Icon = benefit.icon;

    return (
        <article
            className="
        group relative flex h-full flex-col
        rounded-[16px]
        border border-[#E1EEE6]
        bg-white p-6
        shadow-[0_4px_18px_rgba(6,78,59,0.04)]
        transition-all duration-300
        hover:-translate-y-1
        hover:border-[#B8DEC7]
        hover:shadow-[0_14px_32px_rgba(6,78,59,0.10)]
        sm:p-7
      "
        >
            {/* ICON */}
            <div
                className="
          mb-5 flex h-14 w-14
          items-center justify-center
          rounded-[14px] bg-[#E8F7EE]
          text-[#168B43]
          transition-all duration-300
          group-hover:bg-[#168B43]
          group-hover:text-white
        "
            >
                <Icon size={26} strokeWidth={1.8} />
            </div>

            {/* TITLE */}
            <h3
                className="
          text-[16px] font-extrabold
          leading-snug tracking-tight
          text-[#17324D]
          sm:text-[17px]
        "
            >
                {benefit.title}
            </h3>

            {/* DESCRIPTION */}
            <p
                className="
          mt-3 text-[13px]
          leading-[1.8] text-[#64748B]
        "
            >
                {benefit.description}
            </p>

            {/* DECORATIVE GREEN LINE */}
            <div
                className="
          mt-auto pt-6
        "
            >
                <div
                    className="
            h-[3px] w-9 rounded-full
            bg-[#B9E7C9]
            transition-all duration-300
            group-hover:w-16
            group-hover:bg-[#168B43]
          "
                />
            </div>

            {/* SUBTLE CARD NUMBER */}
            <span
                aria-hidden="true"
                className="
          absolute right-6 top-6
          text-[12px] font-bold
          text-[#D2E7DA]
        "
            >
                {String(index + 1).padStart(2, "0")}
            </span>
        </article>
    );
}

export default function WhyChooseUs() {
    return (
        <section
            id="why-choose-us"
            className="
        relative overflow-hidden
        bg-[#F5FAF7]
        py-14 sm:py-16 lg:py-20
      "
        >
            {/* BACKGROUND DECORATION */}
            <div
                aria-hidden="true"
                className="
          pointer-events-none
          absolute -right-32 -top-32
          h-[400px] w-[400px]
          rounded-full bg-[#E0F3E7]/60
          blur-3xl
        "
            />

            <div
                aria-hidden="true"
                className="
          pointer-events-none
          absolute -bottom-40 -left-32
          h-[350px] w-[350px]
          rounded-full bg-[#E4F5EC]/60
          blur-3xl
        "
            />

            {/* SAME WIDTH AS HEADER */}
            <div
                className="
          relative z-10
          mx-auto w-full max-w-[1536px]
          px-4 sm:px-6 lg:px-8
        "
            >
                {/* SECTION HEADING */}
                <div
                    className="
            mx-auto mb-10
            max-w-[760px]
            text-center
            sm:mb-12
          "
                >
                    {/* SMALL LABEL */}
                    <div
                        className="
              mb-3 inline-flex
              items-center justify-center gap-2
            "
                    >
                        <span
                            className="
                h-[2px] w-7
                rounded-full bg-[#168B43]
              "
                        />

                        <span
                            className="
                text-[10px] font-extrabold
                uppercase tracking-[0.2em]
                text-[#168B43]
              "
                        >
                            Why Choose Us
                        </span>

                        <span
                            className="
                h-[2px] w-7
                rounded-full bg-[#168B43]
              "
                        />
                    </div>

                    {/* MAIN HEADING */}
                    <h2
                        className="
              text-[28px] font-extrabold
              leading-tight tracking-[-0.04em]
              text-[#17324D]
              sm:text-[36px] lg:text-[42px]
            "
                    >
                        Your Stay,{" "}
                        <span className="text-[#168B43]">
                            Our Priority
                        </span>
                    </h2>

                    {/* SUBTITLE */}
                    <p
                        className="
              mx-auto mt-4
              max-w-[630px]
              text-[13px] leading-7
              text-[#64748B]
              sm:text-[15px]
            "
                    >
                        Discover a smarter way to explore Dubai.
                        From choosing the right neighborhood to
                        finding a comfortable hotel, we're here
                        to make planning your stay simpler.
                    </p>
                </div>

                {/* BENEFIT CARDS */}
                <div
                    className="
            grid grid-cols-1 gap-4
            sm:grid-cols-2
            lg:grid-cols-3
            lg:gap-5
          "
                >
                    {benefits.map((benefit, index) => (
                        <BenefitCard
                            key={benefit.title}
                            benefit={benefit}
                            index={index}
                        />
                    ))}
                </div>

                {/* BOTTOM HIGHLIGHT STRIP */}
                <div
                    className="
            mt-9 flex flex-col
            items-center justify-between gap-4
            rounded-[16px]
            border border-[#CFE8D8]
            bg-white px-6 py-5
            sm:flex-row sm:px-8
          "
                >
                    <div className="flex items-center gap-3">
                        <span
                            className="
                flex h-11 w-11 shrink-0
                items-center justify-center
                rounded-full bg-[#E8F7EE]
                text-[#168B43]
              "
                        >
                            <Sparkles size={21} />
                        </span>

                        <div>
                            <h3
                                className="
                  text-[14px] font-extrabold
                  text-[#17324D]
                "
                            >
                                Stay Smart. Explore Dubai.
                            </h3>

                            <p
                                className="
                  mt-1 text-[12px]
                  text-[#64748B]
                "
                            >
                                Find the right hotel for your next Dubai adventure.
                            </p>
                        </div>
                    </div>

                    <div
                        className="
              flex flex-wrap items-center
              justify-center gap-x-5 gap-y-2
            "
                    >
                        <span
                            className="
                flex items-center gap-1.5
                text-[11px] font-semibold
                text-[#064E3B]
              "
                        >
                            <CheckCircle2
                                size={16}
                                className="text-[#168B43]"
                            />
                            Easy Search
                        </span>

                        <span
                            className="
                flex items-center gap-1.5
                text-[11px] font-semibold
                text-[#064E3B]
              "
                        >
                            <CheckCircle2
                                size={16}
                                className="text-[#168B43]"
                            />
                            Great Locations
                        </span>

                        <span
                            className="
                flex items-center gap-1.5
                text-[11px] font-semibold
                text-[#064E3B]
              "
                        >
                            <CheckCircle2
                                size={16}
                                className="text-[#168B43]"
                            />
                            Hotel Choices
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
}
